import { useEffect, useState } from 'react';
import {
  ChevronRight,
  CrossIcon,
  Pencil,
  Plus,
  Warning,
} from '../../../../../assets/icons';
import UseContext from '../../../../../hooks/useContext';
import Button from '../../../../button';
import useModal from '../../../../modal/useModal';
import { formatLastUpdate } from 'helper/lastUpdate';
import { isDefaultCoefficientRanges } from 'helper/coefficientRange';

const SlideOverCompanyRemunerationConditions = ({
  companyInfo,
  setCompanyInfo,
  setIsShowCoeff,
  setIsCoeffSend,
  onSaved,
}) => {
  const { toggle } = useModal();
  const { setModalData, modalDataReturn, setModalDataReturn } = UseContext();
  const [isCoeffCompleted, setIsCoeffCompleted] = useState(false);

  useEffect(() => {
    if (companyInfo?.coefficients) {
      if (companyInfo?.coefficients?.length > 0) {
        for (let i = 0; i < companyInfo?.coefficients.length; i++) {
          if (
            companyInfo?.coefficients[i]?.management === null ||
            companyInfo?.coefficients[i]?.delegation === null
          ) {
            setIsCoeffCompleted(false);
            break;
          } else {
            setIsCoeffCompleted(true);
            setIsCoeffSend(true);
          }
        }
      } else {
        setIsCoeffCompleted(false);
      }
    } else {
      setIsCoeffCompleted(false);
    }
  }, [companyInfo]);

  const editOvertime = (idCompany) => {
    setModalData({ idCompany });
    toggle('overtime_company');
  };

  const editNightHours = (idCompany) => {
    setModalData({ idCompany });
    toggle('night_hours_company');
  };

  const editSundayHours = (idCompany) => {
    setModalData({ idCompany });
    toggle('sunday_hours_company');
  };

  const editHolidayHours = (idCompany) => {
    setModalData({ idCompany });
    toggle('holiday_hours_company');
  };

  const groupedNightHours = {};
  const groupedOvertimes = {};

  if (companyInfo?.nightHours) {
    companyInfo.nightHours.forEach((nightHour) => {
      // Group by percentage: entries sharing the same rate are listed together.
      const increaseKey = String(nightHour.percentage ?? 'nc');
      if (!groupedNightHours[increaseKey]) {
        groupedNightHours[increaseKey] = {
          items: [],
        };
      }
      groupedNightHours[increaseKey].items.push(nightHour);
    });
  }
  if (companyInfo?.overtimes) {
    companyInfo.overtimes.forEach((overtime) => {
      // Group by percentage: entries sharing the same rate are listed together.
      const increaseKey = String(overtime.percentage ?? 'nc');
      if (!groupedOvertimes[increaseKey]) {
        groupedOvertimes[increaseKey] = {
          items: [],
        };
      }
      groupedOvertimes[increaseKey].items.push(overtime);
    });
  }

  // nightHours/overtimes are arrays Mongoose defaults to [] (never undefined),
  // so a never-configured company still has a truthy, empty array — gate on
  // length rather than on the array's own truthiness.
  const hasNightHours = (companyInfo?.nightHours ?? []).length > 0;
  const hasOvertimes = (companyInfo?.overtimes ?? []).length > 0;

  // sundayHours/holidayHours are sub-documents ({ bonus, percentage }), not booleans:
  // an empty one ({ bonus: null, percentage: null }) is still truthy, so gate on the
  // percentage actually being set rather than on the sub-document's presence.
  const sundayPercentage = companyInfo?.sundayHours?.percentage;
  const hasSundayHours = sundayPercentage !== undefined && sundayPercentage !== null;
  const sundayPercentageLabel = hasSundayHours ? `${sundayPercentage} %` : 'Non défini';

  const holidayPercentage = companyInfo?.holidayHours?.percentage;
  const hasHolidayHours = holidayPercentage !== undefined && holidayPercentage !== null;
  const holidayPercentageLabel = hasHolidayHours ? `${holidayPercentage} %` : 'Non défini';

  // No flag is stored for "still on default values" (D3): infer it per
  // section instead. Sunday/holiday reuse the presence-based gates above
  // rather than truthiness, since their sub-document is always present.
  const isDefault = (section) => {
    switch (section) {
      case 'coefficients':
        // Creation now persists the real template, so an all-null row set is
        // no longer the only default state — compare against the template.
        return isDefaultCoefficientRanges(
          companyInfo?.coefficients,
          companyInfo?.defaultCoefficients
        );
      case 'overtimes':
        return !hasOvertimes;
      case 'nightHours':
        return !hasNightHours;
      case 'sundayHours':
        return !hasSundayHours;
      case 'holidayHours':
        return !hasHolidayHours;
      default:
        return true;
    }
  };

  // Same tooltip and icon for every section; each key falls back to its
  // "ajout_" counterpart when no "modification_" log exists yet.
  const lastUpdateOf = (modificationKey, ajoutKey) =>
    companyInfo?.lastUpdates?.[modificationKey] ?? companyInfo?.lastUpdates?.[ajoutKey];

  // The alert belongs to the right-hand group, beside the edit button — not
  // inline with the label, where it competed with the description text.
  const renderDefaultIcon = (section) =>
    isDefault(section) ? (
      <span className={'my-auto'} title={'Données par défaut'}>
        <Warning wh={20} color={'#975612'} />
      </span>
    ) : null;

  const renderUpdateStatus = (section, lastUpdate, defaultLabel) => {
    if (isDefault(section)) {
      return <div className={'text-gray-500'}>{defaultLabel}</div>;
    }
    // No log entry at all means the company was filled in before logging
    // existed: show nothing extra rather than a stale/undefined date.
    const label = formatLastUpdate(lastUpdate);
    if (!label) return null;
    return <div className={'text-gray-500'}>{label}</div>;
  };

  useEffect(() => {
    // Set once, not incremented: guards against multiple onSaved calls even
    // if a future added type ends up matching more than one branch per pass.
    let didSave = false;

    if (
      modalDataReturn?.type === 'night_hours_updated' &&
      modalDataReturn?.nightHours
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        nightHours: modalDataReturn.nightHours,
      }));
      didSave = true;
    }
    if (
      modalDataReturn?.type === 'overtimes_updated' &&
      modalDataReturn?.overtimes
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        overtimes: modalDataReturn.overtimes,
      }));
      didSave = true;
    }
    if (
      modalDataReturn?.type === 'sunday_hours_updated' &&
      modalDataReturn?.sundayHours
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        sundayHours: modalDataReturn.sundayHours,
      }));
      didSave = true;
    }
    if (
      modalDataReturn?.type === 'holiday_hours_updated' &&
      modalDataReturn?.holidayHours
    ) {
      setCompanyInfo((prevData) => ({
        ...prevData,
        holidayHours: modalDataReturn.holidayHours,
      }));
      didSave = true;
    }

    // The optimistic setCompanyInfo above keeps the section's own value
    // instant; this refetch is what fills in lastUpdates, since the save
    // endpoints only return the field they touched, not the log entry.
    if (didSave) onSaved?.();

    setModalDataReturn(null);
  }, [modalDataReturn]);

  return (
    <>
      <button
        type={'button'}
        // Browsers centre a button's text and Tailwind's preflight does not
        // reset it, so the short title drifted while the longer lines below
        // filled the width and looked flush left.
        className={'border rounded p-3 text-left'}
        onClick={() => setIsShowCoeff(true)}
      >
        <div className={'flex justify-between'}>
          <div className={'my-auto'}>
            <div>Coefficient de facturation</div>
            {!isCoeffCompleted && (
              <div className={'text-gray-500 flex gap-2'}>
                <div className={'my-auto'}>
                  <CrossIcon wh={20} color={'#EF4444'} />
                </div>
                <div className={'my-auto'}>La page n'est pas complète</div>
              </div>
            )}
            {renderUpdateStatus(
              'coefficients',
              lastUpdateOf(
                'modification_coefficient_facturation',
                'ajout_coefficient_facturation'
              ),
              'Coefficient appliqué par défaut'
            )}
          </div>
          <div className={'my-auto flex items-center gap-2'}>
            {renderDefaultIcon('coefficients')}
            <ChevronRight wh={24} color={'#111827'} />
          </div>
        </div>
      </button>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasOvertimes && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures supplémentaires</div>
            {hasOvertimes && (
              <div className={''}>
                <div className={'text-gray-500'}>
                  Majoration appliquée lorsqu'un intérimaire travaille :
                </div>
                <ul className={'list-disc list-inside'}>
                  {Object.entries(groupedOvertimes).map(
                    ([increaseKey, groupData]) => {
                      const timeRangesElements = [];
                      const overtimesArray = groupData.items || [];

                      overtimesArray.forEach((overtime, index) => {
                        if (index > 0) {
                          timeRangesElements.push(' et ');
                        }
                        timeRangesElements.push(
                          <span key={index} className={'text-gray-900'}>
                            {overtime.start}h
                          </span>
                        );
                      });

                      const percentageLabel =
                        overtimesArray[0]?.percentage !== undefined &&
                        overtimesArray[0]?.percentage !== null
                          ? `${overtimesArray[0].percentage} %`
                          : 'Non défini';

                      return (
                        <li key={increaseKey} className={''}>
                          <span className={'text-gray-500'}>
                            Plus de {timeRangesElements} sur une semaine :{' '}
                            <span className={'text-gray-900'}>{percentageLabel}</span>
                          </span>
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            )}
            {renderUpdateStatus(
              'overtimes',
              lastUpdateOf('modification_heure_supplementaire', 'ajout_heure_supplementaire'),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('overtimes')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editOvertime(companyInfo?._id)}
            >
              {hasOvertimes ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasNightHours && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures de nuit</div>
            {hasNightHours && (
              <div className={''}>
                <div className={'text-gray-500'}>
                  Majoration appliquée lorsqu'un intérimaire travaille :
                </div>
                <ul className={'list-disc list-inside'}>
                  {Object.entries(groupedNightHours).map(
                    ([increaseKey, groupData]) => {
                      const timeRangesElements = [];
                      const nightHoursArray = groupData.items || [];

                      nightHoursArray.forEach((nightHour, index) => {
                        if (index > 0) {
                          timeRangesElements.push(' et ');
                        }
                        timeRangesElements.push(
                          <span key={index} className={'text-gray-900'}>
                            {nightHour.start}h et {nightHour.end}h
                          </span>
                        );
                      });

                      const percentageLabel =
                        nightHoursArray[0]?.percentage !== undefined &&
                        nightHoursArray[0]?.percentage !== null
                          ? `${nightHoursArray[0].percentage} %`
                          : 'Non défini';

                      return (
                        <li key={increaseKey} className={''}>
                          <span className={'text-gray-500'}>
                            Entre {timeRangesElements} :{' '}
                            <span className={'text-gray-900'}>{percentageLabel}</span>
                          </span>
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            )}
            {renderUpdateStatus(
              'nightHours',
              lastUpdateOf('modification_heure_nuit', 'ajout_heure_nuit'),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('nightHours')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editNightHours(companyInfo?._id)}
            >
              {hasNightHours ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasSundayHours && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures du dimanche</div>
            {hasSundayHours && (
              <div className={'text-gray-500'}>
                Majoration appliquée lorsqu'un intérimaire travaille le dimanche
                :{' '}
                <span className={'text-gray-900'}>{sundayPercentageLabel}</span>
              </div>
            )}
            {renderUpdateStatus(
              'sundayHours',
              lastUpdateOf('modification_heure_dimanche', 'ajout_heure_dimanche'),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('sundayHours')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editSundayHours(companyInfo?._id)}
            >
              {hasSundayHours ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className={''}>
        <div
          className={`border rounded p-3 flex justify-between gap-2 ${
            !hasHolidayHours && 'items-center'
          }`}
        >
          <div className={''}>
            <div className={''}>Heures jours fériés travaillés</div>
            {hasHolidayHours && (
              <div className={'text-gray-500'}>
                Majoration appliquée lorsqu'un intérimaire lors d'un jour férié
                :{' '}
                <span className={'text-gray-900'}>{holidayPercentageLabel}</span>
              </div>
            )}
            {renderUpdateStatus(
              'holidayHours',
              lastUpdateOf(
                'modification_heure_jour_ferie_travailles',
                'ajout_heure_jour_ferie_travailles'
              ),
              'Majorations appliquée par défaut'
            )}
          </div>
          <div className={'flex items-center gap-2'}>
            {renderDefaultIcon('holidayHours')}
            <Button
              size={'SMI'}
              color={'SECONDARY'}
              onClick={() => editHolidayHours(companyInfo?._id)}
            >
              {hasHolidayHours ? (
                <Pencil wh={20} color={'#111827'} />
              ) : (
                <Plus wh={20} color={'#111827'} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SlideOverCompanyRemunerationConditions;
